import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GripVertical, Users, Search, X, FilterX } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGES, STAGE_META, SOURCE_META, PRIORITY_META, stageLabel, sourceLabel } from "@/lib/funnel";
import EditLeadDialog from "@/components/EditLeadDialog";
import ProformaDialog from "@/components/ProformaDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CAT_COL = {
  pending: "border-t-amber-400",
  in_transit: "border-t-blue-400",
  won: "border-t-green-400",
  lost: "border-t-slate-400",
  urgent: "border-t-red-500",
};

const LS_KEY = "aaywon:pipeline:view";
const loadView = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
};
const saveView = (patch) => {
  try {
    const cur = loadView();
    localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch { /* ignore */ }
};

function LeadCard({ lead, users, canManage, canDelete, onDragStart, onOpen, onMoveStage, onSetSpoc, onSetPriority, onEdit, onDelete, onProforma }) {
  const src = SOURCE_META[lead.source] || {};
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          draggable
          onDragStart={(e) => onDragStart(e, lead)}
          onClick={() => onOpen(lead)}
          data-testid={`kanban-card-${lead.id}`}
          className={`group bg-white border ${lead.overdue ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"} rounded-lg p-3 shadow-sm hover:shadow-md cursor-pointer transition-all`}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="font-medium text-sm text-slate-900 leading-snug">{lead.company_name}</p>
            <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
          </div>
          {lead.product_requirement && (
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{lead.product_requirement}</p>
          )}
          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${src.color}`}>{sourceLabel(lead)}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PRIORITY_META[lead.priority]?.color}`}>
              {PRIORITY_META[lead.priority]?.label}
            </span>
            {lead.overdue && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700 uppercase">Overdue</span>
            )}
          </div>
          {lead.assigned_to_name && (
            <div className="mt-2.5 flex items-center gap-1.5">
              <div className="h-5 w-5 rounded-full bg-slate-800 text-white text-[9px] flex items-center justify-center font-semibold">
                {lead.assigned_to_name.slice(0, 2).toUpperCase()}
              </div>
              <span className="text-[11px] text-slate-500">{lead.assigned_to_name}</span>
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        <ContextMenuItem onClick={() => onOpen(lead)} data-testid={`ctx-open-${lead.id}`}>Open card</ContextMenuItem>
        <ContextMenuItem onClick={() => onEdit(lead)} data-testid={`ctx-edit-${lead.id}`}>Edit card</ContextMenuItem>
        <ContextMenuItem onClick={() => onProforma(lead)} data-testid={`ctx-proforma-${lead.id}`}>Create proforma invoice</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuSub>
          <ContextMenuSubTrigger data-testid={`ctx-movestage-${lead.id}`}>Move to stage</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            {STAGES.map((s) => (
              <ContextMenuItem key={s} disabled={s === lead.stage} onClick={() => onMoveStage(lead, s)} data-testid={`ctx-stage-${s}`}>
                {stageLabel(s)}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger data-testid={`ctx-spoc-${lead.id}`}>Assign / change SPOC</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            {users.length === 0 && <ContextMenuItem disabled>No team members</ContextMenuItem>}
            {users.map((u) => (
              <ContextMenuItem key={u.id} disabled={u.id === lead.assigned_to} onClick={() => onSetSpoc(lead, u.id)}>
                {u.name} · {u.role === "owner" ? "Admin" : u.role}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger data-testid={`ctx-priority-${lead.id}`}>Change priority</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-40">
            {["high", "medium", "low"].map((p) => (
              <ContextMenuItem key={p} disabled={p === lead.priority} onClick={() => onSetPriority(lead, p)}>
                {PRIORITY_META[p]?.label} Priority
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        {canDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem className="text-red-600 focus:text-red-600" onClick={() => onDelete(lead)} data-testid={`ctx-delete-${lead.id}`}>
              Delete card
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

export default function Pipeline() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [spoc, setSpoc] = useState(() => loadView().spoc || "all");
  const [query, setQuery] = useState(() => loadView().query || "");
  const [editLead, setEditLead] = useState(null);
  const [proformaLead, setProformaLead] = useState(null);
  const scrollRef = useRef(null);
  const restoredRef = useRef(false);
  const navigate = useNavigate();

  const canManage = user?.role === "owner" || user?.role === "manager";
  const canDelete = canManage || !!user?.can_delete;

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  // Persist filters/search
  useEffect(() => { saveView({ spoc, query }); }, [spoc, query]);

  // Restore horizontal scroll once leads have rendered
  useEffect(() => {
    if (restoredRef.current || !leads.length || !scrollRef.current) return;
    const left = loadView().scrollLeft || 0;
    if (left) requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollLeft = left; });
    restoredRef.current = true;
  }, [leads]);

  const onScroll = (e) => saveView({ scrollLeft: e.currentTarget.scrollLeft });

  const clearFilters = () => {
    setSpoc("all");
    setQuery("");
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    saveView({ spoc: "all", query: "", scrollLeft: 0 });
  };
  const hasFilters = spoc !== "all" || query.trim() !== "";

  const q = query.trim().toLowerCase();
  const visible = leads.filter((l) => {
    if (spoc === "unassigned" && l.assigned_to) return false;
    if (spoc !== "all" && spoc !== "unassigned" && l.assigned_to !== spoc) return false;
    if (!q) return true;
    const hay = [
      l.company_name, l.contact_name, l.phone, l.email, l.product_requirement,
      l.source_detail, l.city, l.source, l.assigned_to_name, l.tube_type,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
  const matchCount = q ? visible.length : 0;

  const onDragStart = (e, lead) => setDragId(lead.id);

  const onDrop = async (e, stage) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.stage === stage) return;
    setLeads((prev) => prev.map((l) => (l.id === dragId ? { ...l, stage } : l)));
    try {
      await api.patch(`/leads/${dragId}/stage`, { stage });
      toast.success(`Moved to ${stageLabel(stage)}`);
      load();
    } catch {
      toast.error("Failed to update stage");
      load();
    }
  };

  const moveStage = async (lead, stage) => {
    try {
      await api.patch(`/leads/${lead.id}/stage`, { stage });
      toast.success(`Moved to ${stageLabel(stage)}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to move");
    }
  };

  const setLeadSpoc = async (lead, uid) => {
    try {
      await api.patch(`/leads/${lead.id}`, { assigned_to: uid });
      const u = users.find((x) => x.id === uid);
      toast.success(`Assigned to ${u ? u.name : "member"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to assign");
    }
  };

  const setLeadPriority = async (lead, p) => {
    try {
      await api.patch(`/leads/${lead.id}`, { priority: p });
      toast.success(`Priority set to ${PRIORITY_META[p]?.label}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete "${lead.company_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid="pipeline-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Pipeline</h1>
          <p className="text-slate-500 text-sm">Drag cards across stages, or right-click a card for quick actions</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email, company…"
              data-testid="pipeline-search"
              className="w-72 pl-8 pr-8 bg-white"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="pipeline-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-400" />
            <Select value={spoc} onValueChange={setSpoc}>
              <SelectTrigger className="w-52 bg-white" data-testid="spoc-filter"><SelectValue placeholder="Filter by SPOC" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All SPOCs</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.role === "owner" ? "Admin" : u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button
              variant="outline"
              onClick={clearFilters}
              data-testid="pipeline-clear-filters"
              className="gap-1.5 text-slate-600"
            >
              <FilterX className="h-4 w-4" /> Clear filters
            </Button>
          )}
        </div>
      </div>

      {q && (
        <p className="-mt-2 mb-3 text-xs text-slate-500" data-testid="pipeline-search-count">
          {matchCount} {matchCount === 1 ? "lead" : "leads"} match "{query.trim()}"
        </p>
      )}

      <div ref={scrollRef} onScroll={onScroll} className="flex gap-4 overflow-x-auto pb-4 kanban-scroll">
        {STAGES.map((stage) => {
          const cards = visible.filter((l) => l.stage === stage);
          const meta = STAGE_META[stage];
          return (
            <div
              key={stage}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, stage)}
              data-testid={`kanban-col-${stage}`}
              className="shrink-0 w-72"
            >
              <div className={`bg-slate-100/70 rounded-lg border-t-4 ${CAT_COL[meta.cat]} p-3 min-h-[60vh]`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-sm font-semibold text-slate-700">{meta.label}</span>
                  <span className="text-xs font-medium text-slate-500 bg-white rounded-full px-2 py-0.5">
                    {cards.length}
                  </span>
                </div>
                <div className="space-y-2.5">
                  {cards.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      users={users}
                      canManage={canManage}
                      canDelete={canDelete}
                      onDragStart={onDragStart}
                      onOpen={(l) => navigate(`/leads/${l.id}`)}
                      onMoveStage={moveStage}
                      onSetSpoc={setLeadSpoc}
                      onSetPriority={setLeadPriority}
                      onEdit={(l) => setEditLead(l)}
                      onDelete={removeLead}
                      onProforma={(l) => setProformaLead(l)}
                    />
                  ))}
                  {cards.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-6">No leads</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <EditLeadDialog open={!!editLead} onOpenChange={(v) => !v && setEditLead(null)} lead={editLead} onSaved={load} />
      <ProformaDialog lead={proformaLead} open={!!proformaLead} onOpenChange={(v) => !v && setProformaLead(null)} />
    </div>
  );
}
